import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, money, fmtDate } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { FileBadge2, FileCheck2, FileDown, Info, Pencil, Send, FileX2, XCircle, Copy, ShieldQuestion, RefreshCw } from "lucide-react";

// NFS-e Nacional — Fase 1 (preparação de arquitetura, Produção Restrita).
// Ver docs/NFSE_NACIONAL.md. Emissão real permanece bloqueada nesta fase
// (gap oficial — nunca simulada; 501 explicando o motivo). Cancelamento e
// substituição (delta 2026-09-15) já implementam o fluxo real (evento
// 101101/grupo `subst`, confirmados na documentação oficial), mas
// continuam atrás de gates DESLIGADOS por padrão
// (NFSE_CANCELAMENTO_REAL_ENABLED / NFSE_REAL_EMISSAO_ENABLED) — nenhum
// ambiente do LOOTUS liga isso sem um certificado A1 real.
const MOTIVO_CANCELAMENTO = [
  { codigo: 1, label: "Erro na Emissão" },
  { codigo: 2, label: "Serviço não Prestado" },
  { codigo: 9, label: "Outros" },
];
const MOTIVO_SUBSTITUICAO = [
  { codigo: "01", label: "Desenquadramento de NFS-e do Simples Nacional" },
  { codigo: "02", label: "Enquadramento de NFS-e no Simples Nacional" },
  { codigo: "03", label: "Inclusão Retroativa de Imunidade/Isenção" },
  { codigo: "04", label: "Exclusão Retroativa de Imunidade/Isenção" },
  { codigo: "05", label: "Rejeição pelo tomador/intermediário" },
  { codigo: "99", label: "Outros" },
];
// Estados finais/quase-finais da NFS-e (item 3 do pedido, delta
// 2026-09-16) — badges e mensagens dedicadas para cada um; as AÇÕES
// disponíveis em cada linha da tabela sempre respeitam este mesmo estado
// (nunca é possível cancelar de novo uma nota já cancelada/em análise/
// substituída).
const STATUS_LABEL = {
  rascunho: "Rascunho", validada: "Validada", pendente_envio: "Pendente de envio",
  emitida: "Emitida", rejeitada: "Rejeitada", cancelada: "Cancelada", cancelando: "Cancelando...",
  cancelamento_em_analise: "Cancelamento em análise", substituida: "Substituída",
};
const STATUS_CLASS = {
  rascunho: "bg-slate-100 text-slate-600", validada: "bg-blue-100 text-blue-800",
  pendente_envio: "bg-amber-100 text-amber-800", emitida: "bg-emerald-100 text-emerald-800",
  rejeitada: "bg-red-100 text-red-800", cancelada: "bg-slate-100 text-slate-500",
  cancelando: "bg-amber-100 text-amber-800", cancelamento_em_analise: "bg-amber-100 text-amber-800",
  substituida: "bg-purple-100 text-purple-700",
};

export default function NotasFiscais() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [fiscalActive, setFiscalActive] = useState(null); // null=desconhecido ainda
  const [fiscalCertificado, setFiscalCertificado] = useState(null);
  const [realEmissaoHabilitada, setRealEmissaoHabilitada] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState("");
  const [preparing, setPreparing] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [cancelDoc, setCancelDoc] = useState(null);
  const [cancelMotivoCodigo, setCancelMotivoCodigo] = useState("1");
  const [cancelMotivoDescricao, setCancelMotivoDescricao] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const [substDoc, setSubstDoc] = useState(null);
  const [substMotivoCodigo, setSubstMotivoCodigo] = useState("01");
  const [substMotivoDescricao, setSubstMotivoDescricao] = useState("");
  const [substituting, setSubstituting] = useState(false);

  const [analiseDoc, setAnaliseDoc] = useState(null);
  const [analiseMotivoCodigo, setAnaliseMotivoCodigo] = useState("1");
  const [analiseMotivoDescricao, setAnaliseMotivoDescricao] = useState("");
  const [solicitandoAnalise, setSolicitandoAnalise] = useState(false);

  const load = async () => {
    try {
      const [nf, inv, cli, fiscal] = await Promise.all([
        api.get("/nfse"),
        api.get("/invoices"),
        api.get("/clients"),
        api.get("/config/fiscal/nfse").catch(() => ({ data: null })),
      ]);
      setItems(nf.data);
      setInvoices(inv.data.filter(i => i.status !== "cancelado"));
      setClients(cli.data);
      setFiscalActive(!!fiscal.data?.active);
      setFiscalCertificado(fiscal.data?.certificado || null);
      setRealEmissaoHabilitada(!!fiscal.data?.real_emissao_habilitada);
    } catch { /* nunca deixa a promise sem tratamento */ }
  };
  useEffect(() => { load(); }, []);

  // Item 2 (Ajuste de Produto, 2026-09-15) — só INFORMA o estado já
  // decidido pelo backend (certificado/gate), nunca uma trava nova: o
  // backend (`emitir_nfse`) continua sendo a autoridade e recusa a
  // chamada de qualquer forma (400/501) mesmo que o botão seja habilitado
  // por engano aqui.
  const certificadoValido = !!fiscalCertificado?.configurado
    && fiscalCertificado?.status !== "expirado"
    && fiscalCertificado?.compatible_with_tenant_document !== false;
  const emissaoBloqueadaMsg = !certificadoValido
    ? "Configure um certificado A1 válido para realizar a emissão."
    : !realEmissaoHabilitada
      ? "Configure um certificado A1 válido para realizar a emissão."
      : null;

  const cliName = (id) => clients.find(c => c.id === id)?.nome_fantasia || clients.find(c => c.id === id)?.razao_social || "—";
  const invNumero = (id) => invoices.find(i => i.id === id)?.numero || items.find(n => n.invoice_id === id)?.invoice_id || id?.slice(0, 8);

  const jaTemNfse = (invoiceId) => items.some(n => n.invoice_id === invoiceId && n.status !== "cancelada");
  const invoicesElegiveis = invoices.filter(i => !jaTemNfse(i.id));

  const preparar = async () => {
    if (!selectedInvoice) return toast.error("Selecione uma fatura");
    setPreparing(true);
    try {
      await api.post(`/invoices/${selectedInvoice}/nfse/preparar`);
      toast.success("DPS preparada e validada estruturalmente");
      setSelectedInvoice("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPreparing(false); }
  };

  const validar = async (n) => {
    setBusyId(n.id);
    try {
      const { data } = await api.post(`/nfse/${n.id}/validar`);
      if (data.validation_errors?.length) toast.warning(`DPS com ${data.validation_errors.length} pendência(s) — veja os detalhes`);
      else toast.success("DPS válida (estrutura XSD ok)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const emitir = async (n) => {
    setBusyId(n.id);
    try {
      await api.post(`/nfse/${n.id}/emitir`);
      toast.success("NFS-e emitida");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const baixarXmlPreview = (n) => {
    window.open(`${api.defaults.baseURL}/nfse/${n.id}/dps-xml`, "_blank");
  };
  const baixarXmlOficial = (n) => {
    window.open(`${api.defaults.baseURL}/nfse/${n.id}/xml`, "_blank");
  };
  const baixarPdf = (n) => {
    window.open(`${api.defaults.baseURL}/nfse/${n.id}/pdf`, "_blank");
  };

  const abrirDetalhe = async (n) => {
    try {
      const { data } = await api.get(`/nfse/${n.id}`);
      setDetailOpen(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const abrirCancelamento = (n) => {
    setCancelDoc(n);
    setCancelMotivoCodigo("1");
    setCancelMotivoDescricao("");
  };

  const confirmarCancelamento = async () => {
    if (cancelMotivoDescricao.trim().length < 15) return toast.error("Descreva o motivo do cancelamento (mínimo 15 caracteres)");
    setCancelling(true);
    try {
      await api.post(`/nfse/${cancelDoc.id}/cancelar`, {
        motivo_codigo: Number(cancelMotivoCodigo), motivo_descricao: cancelMotivoDescricao,
      });
      toast.success("NFS-e cancelada");
      setCancelDoc(null);
      load();
    } catch (e) {
      // Gate desligado por padrão nesta fase (501) — mensagem clara, nota continua emitida.
      toast.error(formatApiError(e));
    } finally { setCancelling(false); }
  };

  const abrirSubstituicao = (n) => {
    setSubstDoc(n);
    setSubstMotivoCodigo("01");
    setSubstMotivoDescricao("");
  };

  const confirmarSubstituicao = async () => {
    if (substMotivoDescricao.trim().length < 15) return toast.error("Descreva o motivo da substituição (mínimo 15 caracteres)");
    setSubstituting(true);
    try {
      const { data } = await api.post(`/nfse/${substDoc.id}/substituir`, {
        motivo_codigo: substMotivoCodigo, motivo_descricao: substMotivoDescricao,
      });
      toast.success("Rascunho de substituição criado — edite e emita para concluir");
      setSubstDoc(null);
      navigate(`/notas-fiscais/${data.id}/editar`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubstituting(false); }
  };

  const abrirAnaliseFiscal = (n) => {
    setAnaliseDoc(n);
    setAnaliseMotivoCodigo("1");
    setAnaliseMotivoDescricao("");
  };

  const confirmarAnaliseFiscal = async () => {
    if (analiseMotivoDescricao.trim().length < 15) return toast.error("Descreva o motivo (mínimo 15 caracteres)");
    setSolicitandoAnalise(true);
    try {
      await api.post(`/nfse/${analiseDoc.id}/solicitar-analise-fiscal`, {
        motivo_codigo: Number(analiseMotivoCodigo), motivo_descricao: analiseMotivoDescricao,
      });
      toast.success("Solicitação de análise fiscal enviada");
      setAnaliseDoc(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSolicitandoAnalise(false); }
  };

  const consultarDesfechoAnalise = async (n) => {
    setBusyId(n.id);
    try {
      const { data } = await api.post(`/nfse/${n.id}/analise-fiscal/consultar`);
      if (data.status === "cancelada") toast.success("Cancelamento deferido — NFS-e cancelada");
      else if (data.status === "emitida") toast.warning("Cancelamento indeferido — NFS-e continua emitida");
      else toast.info(data.motivo || "Ainda em análise pela administração tributária municipal");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-3xl font-bold">Notas Fiscais</h1>
      </div>

      {fiscalActive === false && (
        <Card className="p-4 bg-amber-50 border-amber-200 flex items-start gap-2 text-sm text-amber-900">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>
            NFS-e Nacional ainda não está habilitada para esta empresa. Configure em{" "}
            <b>Configurações → Fiscal / NFS-e</b> antes de preparar uma DPS. Esta é a <b>Fase 1</b>
            (preparação/Produção Restrita) — emissão/cancelamento reais ainda dependem de confirmação oficial.
          </div>
        </Card>
      )}

      <Card className="p-5">
        <div className="font-heading font-semibold mb-3 flex items-center gap-2"><FileBadge2 size={18} className="text-primary" /> Preparar NFS-e a partir de uma fatura</div>
        <p className="text-xs text-slate-500 mb-3">
          A NFS-e nasce da FATURA (nunca da ficha) — pelo valor total da fatura, mesmo que ela esteja parcelada
          (uma fatura aglutinada/parcelada gera UMA única DPS).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px]">
            <Select value={selectedInvoice} onValueChange={setSelectedInvoice}>
              <SelectTrigger data-testid="nf-select-invoice"><SelectValue placeholder="Selecione uma fatura" /></SelectTrigger>
              <SelectContent>
                {invoicesElegiveis.map(i => (
                  <SelectItem key={i.id} value={i.id}>{i.numero} — {cliName(i.cliente_id)} — {money(i.valor_total)}</SelectItem>
                ))}
                {invoicesElegiveis.length === 0 && <SelectItem value="__none" disabled>Nenhuma fatura elegível</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={preparar} disabled={preparing || !selectedInvoice} data-testid="nf-preparar">
            {preparing ? "Preparando..." : "Preparar NFS-e"}
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="p-5 pb-3 font-heading font-semibold">Notas fiscais</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="py-3 px-4">Número (DPS)</th><th>Cliente</th><th>Fatura</th><th>Data</th>
                <th>Valor</th><th>Status</th><th>Ambiente</th><th className="text-right pr-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map(n => (
                <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-mono text-xs" title={n.dps_id}>{n.numero_nfse || n.dps_id?.slice(-15)}</td>
                  <td>{cliName(n.cliente_id)}</td>
                  <td>{invNumero(n.invoice_id)}</td>
                  <td>{fmtDate(n.created_at)}</td>
                  <td>{money(n.valor)}</td>
                  <td><Badge className={STATUS_CLASS[n.status] || "bg-slate-100"}>{STATUS_LABEL[n.status] || n.status}</Badge></td>
                  <td><Badge className={n.ambiente === "producao" ? "bg-red-100 text-red-800" : "bg-indigo-100 text-indigo-800"}>
                    {n.ambiente === "producao" ? "Produção" : "Produção Restrita"}
                  </Badge></td>
                  <td className="text-right pr-4 space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(n)} title="Ver DPS" data-testid={`nf-ver-${n.id}`}><Info size={14} /></Button>
                    {(n.status === "rascunho" || n.status === "validada") && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/notas-fiscais/${n.id}/editar`)} title="Editar" data-testid={`nf-editar-${n.id}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="outline" onClick={() => validar(n)} disabled={busyId === n.id} data-testid={`nf-validar-${n.id}`}>
                          <FileCheck2 size={14} /> Validar DPS
                        </Button>
                      </>
                    )}
                    {n.status === "validada" && (
                      <span title={emissaoBloqueadaMsg || undefined}>
                        <Button size="sm" onClick={() => emitir(n)} disabled={busyId === n.id || !!emissaoBloqueadaMsg} data-testid={`nf-emitir-${n.id}`}>
                          <Send size={14} /> Emitir NFS-e
                        </Button>
                      </span>
                    )}
                    {n.status !== "emitida" && n.status !== "cancelada" && n.status !== "substituida" && (
                      <Button size="sm" variant="ghost" onClick={() => baixarXmlPreview(n)} title="XML da DPS" data-testid={`nf-xml-preview-${n.id}`}>
                        <FileDown size={14} /> XML da DPS
                      </Button>
                    )}
                    {/* Item 3 — depois de emitida (ou cancelada/em análise/substituída): Ver | PDF | XML | Cancelar | Substituir */}
                    {(n.status === "emitida" || n.status === "cancelada" || n.status === "substituida" || n.status === "cancelamento_em_analise") && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => baixarPdf(n)} title="PDF (DANFSe)" data-testid={`nf-pdf-${n.id}`}>
                          <FileX2 size={14} /> PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => baixarXmlOficial(n)} title="XML oficial" data-testid={`nf-xml-oficial-${n.id}`}>
                          <FileDown size={14} /> XML
                        </Button>
                      </>
                    )}
                    {/* Ações que mudam de estado só aparecem quando o estado ATUAL permite —
                        nunca é possível cancelar/substituir de novo uma nota já cancelada,
                        em análise fiscal ou substituída. */}
                    {n.status === "emitida" && (
                      <>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => abrirCancelamento(n)} title="Cancelar NFS-e" data-testid={`nf-cancelar-${n.id}`}>
                          <XCircle size={14} /> Cancelar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => abrirAnaliseFiscal(n)} title="Solicitar análise fiscal para cancelamento" data-testid={`nf-analise-fiscal-${n.id}`}>
                          <ShieldQuestion size={14} /> Análise Fiscal
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => abrirSubstituicao(n)} title="Substituir NFS-e" data-testid={`nf-substituir-${n.id}`}>
                          <Copy size={14} /> Substituir
                        </Button>
                      </>
                    )}
                    {n.status === "cancelamento_em_analise" && (
                      <Button size="sm" variant="outline" onClick={() => consultarDesfechoAnalise(n)} disabled={busyId === n.id}
                        title="Consultar desfecho da análise fiscal" data-testid={`nf-consultar-analise-${n.id}`}>
                        <RefreshCw size={14} /> Consultar desfecho
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">Nenhuma NFS-e preparada ainda</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* [Ver] */}
      <Dialog open={!!detailOpen} onOpenChange={v => !v && setDetailOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileBadge2 size={18} className="text-primary" /> DPS {detailOpen?.dps_id}</DialogTitle></DialogHeader>
          {detailOpen && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Status: </span><Badge className={STATUS_CLASS[detailOpen.status]}>{STATUS_LABEL[detailOpen.status] || detailOpen.status}</Badge></div>
                <div><span className="text-slate-500">Valor: </span><b>{money(detailOpen.valor)}</b></div>
                <div><span className="text-slate-500">Série/Número: </span>{detailOpen.serie}/{detailOpen.ndps}</div>
                <div><span className="text-slate-500">Ambiente: </span>{detailOpen.ambiente === "producao" ? "Produção" : "Produção Restrita"}</div>
              </div>
              {detailOpen.validation_errors?.length > 0 && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3">
                  <div className="font-medium text-red-800 mb-1">Pendências da validação estrutural (XSD):</div>
                  <ul className="list-disc pl-5 text-red-700 text-xs space-y-0.5">
                    {detailOpen.validation_errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              {detailOpen.dps_xml_preview && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">XML da DPS (pré-visualização — ainda não assinada/enviada):</div>
                  <pre className="bg-slate-900 text-slate-100 text-[11px] p-3 rounded-md overflow-x-auto max-h-64 whitespace-pre-wrap break-all">{detailOpen.dps_xml_preview}</pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDetailOpen(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [Cancelar NFS-e] — evento 101101, confirmado no Anexo II vigente */}
      <Dialog open={!!cancelDoc} onOpenChange={v => !v && setCancelDoc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><XCircle size={18} /> Cancelar NFS-e</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            O LOOTUS envia o pedido de cancelamento à NFS-e Nacional (evento oficial, assinado digitalmente).
            A nota só muda para "Cancelada" após confirmação do Governo — se houver rejeição, ela continua emitida.
          </p>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Motivo</Label>
              <Select value={cancelMotivoCodigo} onValueChange={setCancelMotivoCodigo}>
                <SelectTrigger data-testid="nf-cancel-motivo-codigo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTIVO_CANCELAMENTO.map(m => <SelectItem key={m.codigo} value={String(m.codigo)}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Descrição do motivo (mínimo 15 caracteres)</Label>
              <Textarea rows={3} value={cancelMotivoDescricao} onChange={e => setCancelMotivoDescricao(e.target.value)} data-testid="nf-cancel-motivo-descricao" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDoc(null)}>Voltar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={confirmarCancelamento} disabled={cancelling} data-testid="nf-cancel-confirmar">
              {cancelling ? "Enviando..." : "Confirmar cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [Substituir NFS-e] — grupo `subst`, confirmado no XSD vendorizado e no Anexo II */}
      <Dialog open={!!substDoc} onOpenChange={v => !v && setSubstDoc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Copy size={18} className="text-primary" /> Substituir NFS-e</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Cria um novo rascunho vinculado a esta NFS-e (chave {substDoc?.chave_acesso?.slice(0, 12)}...).
            Você poderá editar os campos antes de emitir. A NFS-e original só muda para "Substituída"
            depois que a nova for emitida com sucesso — se a emissão falhar, a original continua emitida.
          </p>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Motivo</Label>
              <Select value={substMotivoCodigo} onValueChange={setSubstMotivoCodigo}>
                <SelectTrigger data-testid="nf-subst-motivo-codigo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTIVO_SUBSTITUICAO.map(m => <SelectItem key={m.codigo} value={m.codigo}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Descrição do motivo (mínimo 15 caracteres)</Label>
              <Textarea rows={3} value={substMotivoDescricao} onChange={e => setSubstMotivoDescricao(e.target.value)} data-testid="nf-subst-motivo-descricao" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubstDoc(null)}>Voltar</Button>
            <Button onClick={confirmarSubstituicao} disabled={substituting} data-testid="nf-subst-confirmar">
              {substituting ? "Criando..." : "Criar rascunho de substituição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [Solicitar análise fiscal] — evento 101103, confirmado no Anexo II vigente */}
      <Dialog open={!!analiseDoc} onOpenChange={v => !v && setAnaliseDoc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldQuestion size={18} className="text-primary" /> Solicitar análise fiscal para cancelamento</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Alternativa ao cancelamento direto: a solicitação fica pendente até a administração
            tributária municipal deferir ou indeferir. A nota muda para "Cancelamento em análise" —
            só vira "Cancelada" depois de um deferimento oficial posterior (use "Consultar desfecho"
            na lista para verificar).
          </p>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Motivo</Label>
              <Select value={analiseMotivoCodigo} onValueChange={setAnaliseMotivoCodigo}>
                <SelectTrigger data-testid="nf-analise-motivo-codigo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTIVO_CANCELAMENTO.map(m => <SelectItem key={m.codigo} value={String(m.codigo)}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Descrição do motivo (mínimo 15 caracteres)</Label>
              <Textarea rows={3} value={analiseMotivoDescricao} onChange={e => setAnaliseMotivoDescricao(e.target.value)} data-testid="nf-analise-motivo-descricao" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnaliseDoc(null)}>Voltar</Button>
            <Button onClick={confirmarAnaliseFiscal} disabled={solicitandoAnalise} data-testid="nf-analise-confirmar">
              {solicitandoAnalise ? "Enviando..." : "Solicitar análise fiscal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
